import { Injectable } from '@angular/core';
import { EmployeeTransactionControllerService } from '@iota/iota-management-portal-v4-client';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, of, take } from 'rxjs';
import { getLocationHeader } from 'src/app/shared/utils';
import {
    DeleteTransaction,
    EmployeeTransactionActionTypes,
    LoadTransaction,
    LoadTransactionsByCase,
    UpsertTransaction,
    UpsertTransactionAttributes,
    UpsertTransactionAttributesFailure,
    UpsertTransactionAttributesSuccess,
    UpsertTransactionSuccess
} from '../actions/employee-transaction.actions';
import { UpdateCaseStatusSuccess } from '../actions/employee-case.actions';

@Injectable()
export class EmployeeTransactionEffects {
    loadTransaction$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpsertTransactionSuccess, LoadTransaction),
            mergeMap(({ transactionId }) =>
                this.apiService.getEmployeeTransaction(transactionId).pipe(
                    take(1),
                    map((transaction) => ({ type: EmployeeTransactionActionTypes.LoadTransactionsSuccess, transactions: [transaction] })),
                    catchError((error) => of({ type: EmployeeTransactionActionTypes.LoadTransactionsFailure, error: error }))
                )
            )
        )
    );

    loadTransactionsByCase$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LoadTransactionsByCase),
            mergeMap(({ employeeCaseId }) =>
                this.apiService.getAllEmployeeTransactionsByCase(employeeCaseId).pipe(
                    take(1),
                    map((transactions) => ({ type: EmployeeTransactionActionTypes.LoadTransactionsSuccess, transactions })),
                    catchError((error) => of({ type: EmployeeTransactionActionTypes.LoadTransactionsFailure, error: error }))
                )
            )
        )
    );

    loadTransactionsByCase2$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpdateCaseStatusSuccess),
            mergeMap(({ caseId }) =>
                this.apiService.getAllEmployeeTransactionsByCase(caseId).pipe(
                    take(1),
                    map((transactions) => ({ type: EmployeeTransactionActionTypes.LoadTransactionsSuccess, transactions })),
                    catchError((error) => of({ type: EmployeeTransactionActionTypes.LoadTransactionsFailure, error: error }))
                )
            )
        )
    );

    upsertTransaction$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpsertTransaction),
            mergeMap(({ transaction }) =>
                this.apiService.upsertEmployeeTransaction(transaction, 'response').pipe(
                    take(1),
                    map((response) => ({
                        type: EmployeeTransactionActionTypes.UpsertTransactionSuccess,
                        transactionId: getLocationHeader(response),
                        caseId: transaction.employeeCaseId,
                        subscriptionId: transaction.employeeLedgerId
                    })),
                    catchError((error) => of({ type: EmployeeTransactionActionTypes.UpsertTransactionFailure, error: error }))
                )
            )
        )
    );

    upsertTransactionAttributes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpsertTransactionAttributes),
            mergeMap(({ transactionId, attributes }) =>
                this.apiService.upsertEmployeeTransactionAttributes(transactionId, attributes, 'response').pipe(
                    take(1),
                    map(() => UpsertTransactionAttributesSuccess({ transactionId })),
                    catchError((error) => of(UpsertTransactionAttributesFailure(error)))
                )
            )
        )
    );

    deleteTransaction$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DeleteTransaction),
            mergeMap(({ transaction }) =>
                this.apiService.deleteEmployeeTransaction(transaction.employeeTransactionId).pipe(
                    take(1),
                    map(() => ({
                        type: EmployeeTransactionActionTypes.DeleteTransactionSuccess,
                        transactionId: transaction.employeeTransactionId,
                        caseId: transaction.employeeCaseId,
                        subscriptionId: transaction.employeeLedgerId
                    })),
                    catchError((error) => of({ type: EmployeeTransactionActionTypes.DeleteTransactionFailure, error: error }))
                )
            )
        )
    );

    constructor(
        private actions$: Actions,
        private apiService: EmployeeTransactionControllerService
    ) {}
}
